import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { SignatureRequest } from "./interface";
import {
  EMAIL_PREVIEW_BODY_CONTENT_CLASS,
  EMAIL_PREVIEW_FIELD_ROW_CLASS,
  EMAIL_PREVIEW_HEADER_CLASS,
  EMAIL_PREVIEW_LABEL_CLASS,
  emailPreviewBodyFrameClass,
  emailPreviewFieldClass,
  formatSignatureRequestSender,
} from "./emailPreviewLayout";

interface Props {
  request: SignatureRequest;
  customFormatting?: boolean;
  emailButtonText?: string;
}

const ALLOWED_TAGS = [
  "a", "b", "br", "div", "em", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "span", "strong", "table", "tbody", "td", "th", "thead", "tr", "u", "ul",
];

const DEFAULT_BUTTON_COLOUR = "#1875FB";
const DEFAULT_BUTTON_TEXT_COLOUR = "#ffffff";

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["style", "src", "alt", "href", "target", "rel", "width", "height", "class"],
    WHOLE_DOCUMENT: true,
  });
}

function getSenderName(request: SignatureRequest): string {
  if (request.senderAlias?.aliasName?.trim())
    return request.senderAlias.aliasName.trim();
  if (request.sender?.fullName?.trim())
    return request.sender.fullName.trim();
  return "";
}

function getButtonColours(request: SignatureRequest) {
  return {
    background: request.branding?.colour || DEFAULT_BUTTON_COLOUR,
    color: request.branding?.textColour || DEFAULT_BUTTON_TEXT_COLOUR,
  };
}

function SignatureRequestEmailPreview(props: Props) {
  const { t } = useTranslation();
  const { request, customFormatting, emailButtonText } = props;

  const fieldClass = emailPreviewFieldClass(true);
  const buttonColours = getButtonColours(request);
  const buttonLabel = emailButtonText?.trim() || t("Review and Sign");

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white border border-gray-200 rounded">
      <div className={EMAIL_PREVIEW_HEADER_CLASS}>
        <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
          <label className={EMAIL_PREVIEW_LABEL_CLASS}>{t("From")}:</label>
          <input
            type="text"
            readOnly
            value={formatSignatureRequestSender(request)}
            className={fieldClass}
          />
        </div>

        <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
          <label className={EMAIL_PREVIEW_LABEL_CLASS}>{t("To")}:</label>
          <input
            type="text"
            readOnly
            value={request.recipientEmail || getRecipientDisplay(request)}
            className={fieldClass}
          />
        </div>
        {!!request.emailCc?.trim() &&
          <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
            <label className={EMAIL_PREVIEW_LABEL_CLASS}>{t("Cc")}:</label>
            <input type="text" readOnly value={request.emailCc} className={fieldClass} />
          </div>
        }
        <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
          <label className={EMAIL_PREVIEW_LABEL_CLASS}>{t("Subject")}:</label>
          <input
            type="text"
            readOnly
            value={request.emailSubject || ""}
            className={fieldClass}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 px-4 pt-1 pb-4">
        <div className={emailPreviewBodyFrameClass(true)}>
          {customFormatting ? (
            <div
              className={EMAIL_PREVIEW_BODY_CONTENT_CLASS}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(request.emailContent || "") }}
            />
          ) : (
            <div className={EMAIL_PREVIEW_BODY_CONTENT_CLASS}>
              <div className="bg-white rounded-md border border-gray-100 p-4 text-gray-700">
                <h1 className="text-base font-semibold text-black mb-3">{t("Signature Request")}</h1>
                {!!getSenderName(request) &&
                  <p className="mb-3 text-gray-600">
                    {t("{{name}} has requested you sign the following documents.", { name: getSenderName(request) })}
                  </p>
                }
                <div
                  className="my-3 text-gray-600"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(request.emailContent || "") }}
                />
                <span
                  className="inline-block font-semibold px-10 py-2 rounded text-sm my-3 select-none"
                  style={{ backgroundColor: buttonColours.background, color: buttonColours.color }}
                >
                  {buttonLabel}
                </span>
                <hr className="border-gray-200 my-3" />
                <p className="text-xs font-bold text-amber-600">
                  {t("Warning: To prevent others from accessing your document, please do not forward this email.")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getRecipientDisplay(request: SignatureRequest): string {
  if (request.recipientName?.trim())
    return request.recipientName.trim();
  return "";
}

export default SignatureRequestEmailPreview;
